import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import EMIsAPI from "../../services/emi-api";
import { getLeadID } from "../../utils/storage";
import { handleShowContextualLoader, handlehideContextualLoader } from "../../utils/contextualLoaderTrigger";
import { paymentStatusEnum } from "../../Constants/constant";
import { MdDone, MdClose, MdOutlinePendingActions } from "react-icons/md";
import { PREVIOUS_ROUTE_LOCAL_STORAGE_KEY, PAYMENT_RESPONSE_LOCAL_STORAGE_KEY, SELECTED_PAYMENT_GATEWAY, PAYMENT_GATEWAY } from "../../Constants/constant";

import styles from "./paymentStatus.module.scss";

export default function RepaymentStatus() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const { verifyPayment } = new EMIsAPI();
  const paymentResponse = useSelector((state) => state.user?.paymentResponse);
  let {txnStatus, ...payload} = paymentResponse;
  const isSuccess = txnStatus?.toLowerCase() === "success";
  const prevRoute = localStorage.getItem(PREVIOUS_ROUTE_LOCAL_STORAGE_KEY) || '/';
  const selectedPaymentGateway = localStorage.getItem(SELECTED_PAYMENT_GATEWAY);

  useEffect(() => {
    let attempts = 0;
    let isCancelled = false; // ← cancellation flag
    const delay = 3000;
    const maxAttempts = Math.floor((1 * 60 * 1000) / delay); // stop after ~1 minute (20 * 3s)
    const poll = async () => {
      if (isCancelled) return;
      handleShowContextualLoader(<div style={{fontSize: "16px"}}>We’re checking the status with the bank.<br /><span style={{fontWeight: 300, fontSize: "12px"}}>Please don’t press back or exit the page...</span></div>);
      attempts += 1;
      
      try {
        const res = await verifyPayment(payload, selectedPaymentGateway);
        if (isCancelled) return;
        if ((attempts === maxAttempts || (res?.data && res.data.status !== paymentStatusEnum.PENDING))) {
          setData(res.data);
          handlehideContextualLoader();
          return; // stop polling when we get data
        }
      } catch (error) {
        console.error("Error fetching payment status:", error);
        handlehideContextualLoader();
        return;
      }

      if (attempts < maxAttempts) {
        setTimeout(poll, delay);
      } else {
        handlehideContextualLoader();
      }
    };

    // start immediate poll
    if(txnStatus !== 'cancel' && Object.keys(payload).length > 1 &&  (payload.txnid || (payload.paymentID && payload.orderID))) {
      poll();
    } else {
      navigate("/", { replace: true });
    }
    return () => {
      isCancelled = true; // ← cancels the loop on unmount
    };
  }, []);

  const ClearStoredPaymentResponse = () => {
    localStorage.removeItem(PAYMENT_RESPONSE_LOCAL_STORAGE_KEY);
    localStorage.removeItem(PREVIOUS_ROUTE_LOCAL_STORAGE_KEY);
  };

  if (!data) {
    return null;
  }

  return <PaymentStatus status={data?.status} isEligibleForReloan={data?.isEligibleForReloan} />;
}

const PaymentStatus = ({ status, isEligibleForReloan = false }) => {
  const navigate = useNavigate();
  const isRepeatCustomer = localStorage.getItem("customerType") !== "New Customer";
  return (
    <div className={`${styles.container} ${styles["flex-column"]}`}>
      <header className={`${styles.header} ${styles["flex-center"]} ${styles["flex-column"]}`}>
        <div className={`${styles["tick-icon"]} ${styles["status-" + status]} ${styles["flex-center"]}`}>
          {status === paymentStatusEnum.SUCCESS && <MdDone size={64} color="white" />}
          {status === paymentStatusEnum.FAILURE && <MdClose size={64} color="white" />}
          {status === paymentStatusEnum.PENDING && <MdOutlinePendingActions size={64} color="white" />}
        </div>
        <h1 className={`${styles.mainHeading} ${styles["status-" + status]}`}>
          {status === paymentStatusEnum.SUCCESS && "Payment Successful"}
          {status === paymentStatusEnum.FAILURE && "Payment Failed"}
          {status === paymentStatusEnum.PENDING && "Payment Pending"}
        </h1>
        {status === paymentStatusEnum.FAILURE && <p>Unfortunately, your payment could not be processed.</p>}
        {status === paymentStatusEnum.PENDING && <p>Your payment is currently pending. We will update the status once the bank confirms the transaction.</p>}
      </header>

      <footer className={styles.footer}>
        {isEligibleForReloan && (
          <div className={styles.card}>
            <h2>🎉 You're eligible for a new loan!</h2>
            <a className={styles.btn} onClick={() => navigate("/reloan")}>
              Get Loan Now
            </a>
          </div>
        )}
        {!isEligibleForReloan && (
          <a className={styles.btn} onClick={() => navigate("/")}>
            Back to Home
          </a>
        )}
        {/* <div className={styles["footer-note"]}>Please do not press back or close the app</div> */}
      </footer>
    </div>
  );
};
